import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Phone,
  MapPin,
  Navigation,
  Calendar,
  Clock,
  Accessibility,
  Route as RouteIcon,
  StickyNote,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Printer,
  History,
} from 'lucide-react';

// Canonical status options (user-facing labels). Historical values are read
// but not offered as choices from this UI.
const STATUS_OPTIONS = [
  { value: 'Pending Dispatch Review', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'Contacted',               color: 'bg-sky-100 text-sky-800 border-sky-300' },
  { value: 'Confirmed',               color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'Declined',                color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'Cancelled',               color: 'bg-rose-100 text-rose-800 border-rose-300' },
  { value: 'Completed',               color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
];

const REASON_REQUIRED = new Set(['Declined', 'Cancelled']);

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status);
  const color = opt?.color || 'bg-gray-100 text-gray-800 border-gray-300';
  return (
    <span
      data-testid="detail-current-status"
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${color}`}
    >
      {status}
    </span>
  );
}

/**
 * Open a map for a given address, favouring the user's native maps app on
 * mobile (via geo:) with a Google Maps web fallback. The address is only sent
 * to a mapping provider after a deliberate tap by an authorized staff member.
 */
function openMap(address) {
  if (!address) return;
  const encoded = encodeURIComponent(address);
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  // Prefer native maps on iOS/Android; fall back to Google Maps web link.
  let url;
  if (isIOS) {
    // Apple Maps handles both http links and maps:// scheme.
    url = `maps://?daddr=${encoded}`;
  } else if (isAndroid) {
    // Android intent — opens the default maps app; falls back to browser.
    url = `geo:0,0?q=${encoded}`;
  } else {
    url = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  }
  // Attempt native scheme, fall back to Google Maps web after a short delay if
  // no app intercepted (e.g., Chrome on Android with no maps app installed).
  const webFallback = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  try {
    window.location.href = url;
    if (isIOS || isAndroid) {
      setTimeout(() => {
        // If the page is still visible, the scheme failed → open web.
        if (!document.hidden) {
          window.open(webFallback, '_blank', 'noopener');
        }
      }, 800);
    }
  } catch {
    window.open(webFallback, '_blank', 'noopener');
  }
}

function callPhone(phone) {
  if (!phone) return;
  const digits = String(phone).replace(/[^0-9+]/g, '');
  window.location.href = `tel:${digits}`;
}

const DispatchBookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  // Draft (edited) values — kept separately so failed saves preserve them.
  const [draftStatus, setDraftStatus] = useState('');
  const [draftReason, setDraftReason] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // {ok:bool, msg:string}

  const fetchBooking = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotFound(false);
    setForbidden(false);
    try {
      const res = await authFetch(`/api/bookings/${id}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (res.status === 403) { setForbidden(true); return; }
      if (!res.ok) {
        setError(`Unable to load request (HTTP ${res.status}).`);
        return;
      }
      const data = await res.json();
      setBooking(data);
      setDraftStatus(data.status || 'Pending Dispatch Review');
      setDraftReason('');
      setDraftNotes(data.internal_notes || '');
    } catch (e) {
      setError('Network error while loading the request. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Auth is enforced server-side; authFetch handles the 401 redirect.
    fetchBooking();
  }, [id, navigate, fetchBooking]);

  const statusChanged = booking && draftStatus !== booking.status;
  const notesChanged = booking && (draftNotes || '') !== (booking.internal_notes || '');
  const dirty = statusChanged || notesChanged;
  const requiresReason = REASON_REQUIRED.has(draftStatus) && draftStatus !== booking?.status;
  const canSave = dirty && !saving && (!requiresReason || draftReason.trim().length > 0);

  const handleSave = async () => {
    if (!booking || saving || !dirty) return;
    setSaving(true);
    setSaveResult(null);

    const results = { status: null, notes: null };
    let latestUpdatedAt = booking.updated_at;

    try {
      if (statusChanged) {
        const res = await authFetch(`/api/bookings/${booking.id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({
            status: draftStatus,
            reason: requiresReason ? draftReason.trim() : undefined,
            if_updated_at: latestUpdatedAt,
          }),
        });
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          setSaveResult({ ok: false, msg: body?.detail?.message || 'This request was updated by someone else. Reload and try again.' });
          setSaving(false);
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const msg = typeof body.detail === 'string' ? body.detail : (body?.detail?.message || 'Status update failed.');
          setSaveResult({ ok: false, msg });
          setSaving(false);
          return;
        }
        const updated = await res.json();
        results.status = updated;
        latestUpdatedAt = updated.updated_at;
      }

      if (notesChanged) {
        const res = await authFetch(`/api/bookings/${booking.id}/notes`, {
          method: 'PATCH',
          body: JSON.stringify({
            internal_notes: draftNotes,
            if_updated_at: latestUpdatedAt,
          }),
        });
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          setSaveResult({ ok: false, msg: body?.detail?.message || 'This request was updated by someone else. Reload and try again.' });
          setSaving(false);
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const msg = typeof body.detail === 'string' ? body.detail : 'Notes update failed.';
          setSaveResult({ ok: false, msg });
          setSaving(false);
          return;
        }
        const updated = await res.json();
        results.notes = updated;
      }

      const final = results.notes || results.status;
      if (final) {
        setBooking(final);
        setDraftStatus(final.status);
        setDraftNotes(final.internal_notes || '');
        setDraftReason('');
      }
      setSaveResult({ ok: true, msg: 'Changes saved.' });
      toast.success('Changes saved.');
    } catch (e) {
      setSaveResult({ ok: false, msg: 'Network error. Your changes are preserved — try Save again.' });
    } finally {
      setSaving(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center" data-testid="detail-loading">
          <Loader2 className="mx-auto w-8 h-8 animate-spin text-[#004C97]" />
          <p className="mt-3 text-gray-600 text-sm">Loading request…</p>
        </div>
      </div>
    );
  }

  if (notFound || forbidden || (!booking && error)) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <TopBar onBack={() => navigate('/dispatch/bookings')} />
        <main className="flex-1 max-w-lg mx-auto w-full px-4 py-8">
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <AlertCircle className="mx-auto w-10 h-10 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900" data-testid="detail-error-title">
                {notFound ? 'Request not found' : forbidden ? 'Not authorized' : 'Unable to load'}
              </h2>
              <p className="text-sm text-gray-600" data-testid="detail-error-message">
                {notFound
                  ? 'This transportation request could not be found. It may have been removed.'
                  : forbidden
                  ? 'Your account does not have permission to view this request.'
                  : error}
              </p>
              <Button
                onClick={() => navigate('/dispatch/bookings')}
                className="w-full min-h-[48px] bg-[#004C97] hover:bg-[#003a78] text-white"
                data-testid="detail-error-back-btn"
              >
                Back to Transportation Requests
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <TopBar onBack={() => navigate('/dispatch/bookings')} onRefresh={fetchBooking} />

      <main
        className="max-w-2xl mx-auto w-full px-4 py-5 space-y-4"
        data-testid="dispatch-booking-detail"
      >
        {/* Header card: ref, status, submitted */}
        <Card>
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Reference</p>
                <p className="text-xl font-bold text-[#004C97]" data-testid="detail-ref">
                  {booking.ref || booking.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
              <StatusBadge status={booking.status} />
            </div>
            <div className="text-xs text-gray-500">
              Submitted {formatDateTime(booking.created_at)}
            </div>
          </CardContent>
        </Card>

        {/* Primary action: Call Customer (appears only after data loaded) */}
        <Button
          onClick={() => callPhone(booking.contact_phone)}
          className="w-full min-h-[56px] bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold shadow-md"
          data-testid="detail-call-customer-btn"
        >
          <Phone className="w-5 h-5 mr-2" />
          Call {booking.contact_phone}
        </Button>

        {/* Driver Trip Sheet — available only when Confirmed (server enforces) */}
        <Button
          onClick={() => navigate(`/dispatch/bookings/${booking.id}/trip-sheet`)}
          disabled={booking.status !== 'Confirmed'}
          variant="outline"
          className="w-full min-h-[52px] border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white disabled:opacity-50"
          data-testid="detail-trip-sheet-btn"
          title={booking.status !== 'Confirmed'
            ? 'Available only for Confirmed requests (admin override required otherwise).'
            : 'Print the driver trip sheet'}
        >
          <Printer className="w-5 h-5 mr-2" />
          Print Driver Trip Sheet
        </Button>

        {/* Trip details */}
        <SectionCard title="Trip Details" testid="detail-trip-section">
          <DetailRow icon={<Calendar className="w-4 h-4" />} label="Date"
                     value={booking.date} testid="detail-date" />
          <DetailRow icon={<Clock className="w-4 h-4" />} label="Pickup Time"
                     value={booking.time} testid="detail-time" />
          <DetailRow icon={<Accessibility className="w-4 h-4" />} label="Mobility Type"
                     value={booking.mobility_type} testid="detail-mobility" />
          <DetailRow icon={<RouteIcon className="w-4 h-4" />} label="Trip Type"
                     value={friendlyTripType(booking.trip_type)} testid="detail-trip-type" />
          {booking.trip_type === 'round_trip' && (
            <DetailRow icon={<Clock className="w-4 h-4" />} label="Return Time"
                       value={booking.return_time_unknown ? 'To be determined' : (booking.return_time || '—')}
                       testid="detail-return-time" />
          )}
          {booking.facility_name && (
            <DetailRow label="Facility" value={booking.facility_name} testid="detail-facility" />
          )}
          <DetailRow label="Patient" value={booking.patient_name} testid="detail-patient" />
          {booking.contact_email && (
            <DetailRow label="Contact Email" value={booking.contact_email} testid="detail-email" />
          )}
        </SectionCard>

        {/* Addresses — tap to open maps */}
        <SectionCard title="Pickup & Destination" testid="detail-addresses-section">
          <AddressBlock
            label="Pickup Address"
            address={booking.pickup_address}
            unit={booking.pickup_unit}
            onNavigate={() => openMap(fullAddress(booking.pickup_address, booking.pickup_unit))}
            testid="detail-pickup"
          />
          <div className="border-t border-gray-100 my-2" />
          <AddressBlock
            label="Destination"
            address={booking.dropoff_address}
            unit={booking.destination_unit}
            onNavigate={() => openMap(fullAddress(booking.dropoff_address, booking.destination_unit))}
            testid="detail-destination"
          />
        </SectionCard>

        {/* Special instructions */}
        {booking.special_instructions && (
          <SectionCard title="Special Instructions" testid="detail-instructions-section">
            <p className="text-sm text-gray-800 whitespace-pre-wrap" data-testid="detail-special-instructions">
              {booking.special_instructions}
            </p>
          </SectionCard>
        )}

        {/* Status update */}
        <SectionCard title="Update Status" testid="detail-status-section">
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Request status">
            {STATUS_OPTIONS.map((opt) => {
              const selected = draftStatus === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setDraftStatus(opt.value)}
                  data-testid={`detail-status-option-${opt.value.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`min-h-[48px] px-3 py-2 rounded-lg border text-sm font-medium transition-colors
                    ${selected
                      ? 'border-[#004C97] bg-[#004C97] text-white shadow-sm'
                      : 'border-gray-300 bg-white text-gray-800 hover:border-[#004C97]'
                    }`}
                >
                  {opt.value}
                </button>
              );
            })}
          </div>

          {requiresReason && (
            <div className="mt-3">
              <label className="text-xs font-semibold text-gray-700">
                Reason for {draftStatus} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={draftReason}
                onChange={(e) => setDraftReason(e.target.value)}
                rows={3}
                data-testid="detail-reason-input"
                placeholder={`Why is this request being marked ${draftStatus}? (internal only)`}
                className="mt-1 w-full min-h-[80px] px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
              />
            </div>
          )}
        </SectionCard>

        {/* Internal note */}
        <SectionCard title="Internal Dispatch Note" testid="detail-notes-section">
          <p className="text-xs text-gray-500 mb-2">
            <StickyNote className="w-3 h-3 inline mr-1" />
            Visible only to dispatch staff — never shared with the customer.
          </p>
          <textarea
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            rows={4}
            data-testid="detail-notes-input"
            placeholder="Add or update the internal dispatch note…"
            className="w-full min-h-[100px] px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
          />
        </SectionCard>

        {/* Activity History (audit trail) */}
        <AuditHistory bookingId={booking.id} />

        {/* Inline save result */}
        {saveResult && (
          <div
            data-testid="detail-save-result"
            className={`rounded-lg p-3 text-sm border ${
              saveResult.ok
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            {saveResult.msg}
          </div>
        )}
      </main>

      {/* Sticky bottom Save bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/dispatch/bookings')}
            className="min-h-[48px] px-4"
            data-testid="detail-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="detail-save-btn"
            className="flex-1 min-h-[48px] bg-[#F9A825] hover:bg-[#E08E1E] text-white font-semibold disabled:opacity-50"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Small local components ───────────────────────────────────────────────
function TopBar({ onBack, onRefresh }) {
  return (
    <header className="bg-[#004C97] shadow-sm sticky top-0 z-20">
      <div className="max-w-2xl mx-auto px-3 py-3 flex items-center justify-between">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-white hover:bg-white/10 min-h-[44px] px-2"
          data-testid="detail-topbar-back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          <span className="hidden sm:inline">Transportation Requests</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <h1 className="text-white font-semibold text-base sm:text-lg">Request Details</h1>
        {onRefresh ? (
          <Button
            onClick={onRefresh}
            variant="ghost"
            className="text-white hover:bg-white/10 min-h-[44px] px-2"
            data-testid="detail-topbar-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        ) : (
          <div className="w-10" />
        )}
      </div>
    </header>
  );
}

function SectionCard({ title, children, testid }) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5" data-testid={testid}>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

function DetailRow({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={testid}>
      <div className="text-gray-400 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 break-words">{value || '—'}</p>
      </div>
    </div>
  );
}

function AddressBlock({ label, address, unit, onNavigate, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs text-gray-500 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> {label}
      </p>
      <p className="text-sm text-gray-900 mt-1 break-words" data-testid={`${testid}-address`}>
        {address}{unit ? `, Unit ${unit}` : ''}
      </p>
      <Button
        onClick={onNavigate}
        variant="outline"
        className="mt-2 w-full min-h-[44px] border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white"
        data-testid={`${testid}-navigate-btn`}
      >
        <Navigation className="w-4 h-4 mr-2" />
        Open in Maps
      </Button>
    </div>
  );
}

function fullAddress(addr, unit) {
  if (!addr) return '';
  return unit ? `${addr}, Unit ${unit}` : addr;
}

function friendlyTripType(tt) {
  if (!tt) return '—';
  const map = {
    one_way: 'One-way',
    round_trip: 'Round-trip',
    wait_and_return: 'Wait & Return',
  };
  return map[tt] || tt;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

/**
 * Read-only, collapsible audit history. Fetches only when opened and never
 * exposes internal notes text, PHI, or free-text special instructions —
 * only status transitions, actor, and timestamps served by the backend.
 */
function AuditHistory({ bookingId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/api/bookings/${bookingId}/audit-logs`);
      if (!res.ok) {
        setError(`Unable to load activity (HTTP ${res.status}).`);
        return;
      }
      const data = await res.json();
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch (e) {
      setError('Network error loading activity.');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (open && entries === null) load();
  }, [open, entries, load]);

  return (
    <Card>
      <CardContent className="p-4 sm:p-5" data-testid="detail-audit-section">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="detail-audit-toggle"
          aria-expanded={open}
          className="w-full flex items-center justify-between min-h-[44px] text-left"
        >
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
            <History className="w-4 h-4" /> Activity History
            {entries && (
              <span className="text-xs font-normal text-gray-500 normal-case">
                ({entries.length})
              </span>
            )}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </button>

        {open && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">
                Newest first. Records are written server-side and cannot be edited.
              </p>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-xs text-[#004C97] hover:underline disabled:opacity-50"
                data-testid="detail-audit-refresh"
              >
                Refresh
              </button>
            </div>
            {loading && (
              <div className="text-center py-4" data-testid="detail-audit-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#004C97]" />
              </div>
            )}
            {error && (
              <p className="text-sm text-red-700" data-testid="detail-audit-error">{error}</p>
            )}
            {!loading && !error && entries && entries.length === 0 && (
              <p className="text-sm text-gray-500" data-testid="detail-audit-empty">
                No recorded activity yet.
              </p>
            )}
            {!loading && !error && entries && entries.length > 0 && (
              <ul className="space-y-2" data-testid="detail-audit-list">
                {entries.map((e) => (
                  <li
                    key={e.id}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    data-testid={`detail-audit-entry-${e.action}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {actionLabel(e)}
                      </p>
                      <p className="text-xs text-gray-500 whitespace-nowrap">
                        {e.at_display}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      by <span className="font-medium">{e.actor_username || 'system'}</span>
                    </p>
                    {(e.reason || e.override_reason) && (
                      <p className="text-xs text-gray-700 mt-1 italic">
                        Reason: {e.reason || e.override_reason}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function actionLabel(e) {
  if (e.action === 'status_change') {
    const from = e.previous_status || '—';
    const to = e.new_status || '—';
    return `Status changed: ${from} → ${to}`;
  }
  if (e.action === 'notes_update') {
    return e.note_added_or_updated === 'added'
      ? 'Internal note added'
      : 'Internal note updated';
  }
  if (e.action === 'trip_sheet_generated') {
    return e.override_reason
      ? 'Trip sheet generated (admin override)'
      : 'Trip sheet generated';
  }
  return e.action || 'Activity';
}

export default DispatchBookingDetail;
